import React, { useState } from 'react';
import { FaRoute } from "react-icons/fa";
import { useNavigate, useLocation } from 'react-router-dom';
import { openBookingTab, deliverBookingTab, cancelBookingTab } from '../../../utils/bookingTab';
import { getAuth } from 'firebase/auth';
import { useAuth } from '../../../context/AuthContext';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Navigation, Keyboard, Mousewheel } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';

import { Train, Bus, ChevronDown, ChevronUp, Info, ExternalLink, Map as MapIcon, Share2 } from 'lucide-react';
import LoadingModal from '../../modal/LoadingModal';
import { AvailabilityNote, AltBookingBanner } from './AvailabilityNote';
import SeatCheckingStatus, { SeatSpinner, SeatRefreshIcon, SeatRefiningHint } from './SeatCheckingStatus';
import { requestAvailability, segAvailKeyOf } from '../../../services/availabilityQueue';
import { useAvailStatuses } from '../../../hooks/useSeatAvailability';
import { reqKeyOf, availEntryOf, isBusyStatus, toFareNum } from '../../../utils/seatAvailability';
import { fetchFareQuote, FallbackAvailableError } from '../../../services/flightBookingAPI';


import './FlightMultiResultCard.css';
import CabOptions, { defaultCabCategory, selectedCabPrice } from './CabOptions';

const firstWord = (value) => {
    const text = String(value || '').trim();
    return text ? text.split(/\s+/)[0] : '';
};
const toTitleCase = (str) => str ? str.replace(/\b\w/g, c => c.toUpperCase()) : str;

const getBusPromoDesc = (card) => {
    const busLeg = (card?.legs || []).find((leg) => {
        const type = String(leg?.type || leg?.mode || '').toLowerCase();
        return type === 'bus' || type.includes('bus');
    });
    return busLeg?.flat_off_flight_promo_desc || card?.flat_off_bus_promo_desc || null;
};

const getLegStationName = (leg, side) => {
    const type = String(leg?.type || leg?.mode || '').toLowerCase();
    const rawName = side === 'origin'
        ? (leg?.origin?.name || leg?.origin?.origin_name || leg?.origin_name)
        : (leg?.destination?.name || leg?.destination?.dest_name || leg?.dest_name);
    return type === 'bus' || type.includes('bus') ? firstWord(rawName) : rawName;
};

const compareClasses = (c1, c2) => {
    if (!c1 || !c2) return false;
    const normalize = (s) => String(s).replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    return normalize(c1) === normalize(c2);
};

const SeatAvailabilityGrid = ({ leg, handleRefresh, onClassChange, actionButton }) => {
    const availableClasses = leg?.seat_classes?.filter(cls => cls !== 'GN') || [];
    const getDefaultClass = (classes) => {
        const ui = leg?.seat_class_ui;
        if (ui && classes.some(c => compareClasses(c, ui))) return ui;
        return classes.length > 0 ? classes[0] : null;
    };
    const [selectedClass, setSelectedClass] = React.useState(() => getDefaultClass(availableClasses));
    const userPickedRef = React.useRef(false);

    // Follow the shown class as live availability changes it, unless the user picked a tile
    React.useEffect(() => {
        if (userPickedRef.current) return;
        const classes = leg?.seat_classes?.filter(cls => cls !== 'GN') || [];
        const ui = leg?.seat_class_ui;
        if (ui && classes.some(c => compareClasses(c, ui))) {
            setSelectedClass(ui);
        } else if (classes.length > 0) {
            setSelectedClass(classes[0]);
        }
    }, [leg?.seat_class_ui, leg?.seat_classes]);

    // Every seat class cell tracks its own availability call
    const trainSeg = leg?.train_segment || null;
    const availStatuses = useAvailStatuses(trainSeg ? availableClasses.map(c => reqKeyOf(trainSeg, c)) : []);
    const isClassBusy = (cls) => !!trainSeg && isBusyStatus(availStatuses[reqKeyOf(trainSeg, cls)]);

    if (availableClasses.length === 0) return null;

    const getClassData = (cls, idx) => {
        const uniqueKey = `${leg.number}_${idx}`;
        const refreshed = availEntryOf(leg.avail_by_class, cls);
        const busy = isClassBusy(cls);
        const uiMatch = leg.seat_avail_ui && compareClasses(leg.seat_avail_ui.seat_class, cls);
        let availInfo = refreshed ? refreshed.avl : (uiMatch ? leg.seat_avail_ui.avl : (leg.seat_avail?.[cls] || null));
        let cost = refreshed ? refreshed.fare : (leg.seat_costs?.[cls] || null);

        if (!cost || cost === '--') {
            if (uiMatch) {
                cost = leg.seat_avail_ui.fare || null;
            } else if (leg.seat_avail && leg.seat_avail.fare && (compareClasses(leg.seat_class_ui, cls) || compareClasses(leg.seat_avail.seat_class, cls))) {
                cost = leg.seat_avail.fare || null;
            }
        }

        let foundQuota = refreshed ? refreshed.quota : null;
        if (!foundQuota) {
            if (leg.seat_avail_ui && compareClasses(leg.seat_avail_ui.seat_class, cls)) {
                foundQuota = leg.seat_avail_ui.quota;
            } else if (leg.seat_avail && (compareClasses(leg.seat_class_ui, cls) || (leg.seat_avail.seat_class && compareClasses(leg.seat_avail.seat_class, cls)))) {
                foundQuota = leg.seat_avail.quota;
            }
        }

        let displayCls = cls;
        let hasData = (cost && cost !== '--') || (availInfo && availInfo !== 'N/A' && availInfo !== null);
        let displayQuota = hasData ? (foundQuota || leg.category || 'GN') : 'N/A';

        const quotaCodes = ['GN', 'TQ', 'PT', 'SS', 'LD', 'DF', 'FT'];
        if ((displayQuota.toLowerCase().includes('reserved') || displayQuota === 'Reserved') && quotaCodes.includes(displayCls.toUpperCase())) {
            displayQuota = 'N/A';
            displayCls = leg.train_class || 'N/A';
        }

        if (displayQuota.toLowerCase().includes('reserved')) {
            displayQuota = 'GN';
        }

        const availLower = availInfo?.toLowerCase() || '';
        const isAvbl = (availLower.includes('avail') || availLower.includes('avl') || availLower.includes('avbl')) && !availLower.includes('not');
        const isRac = availLower.includes('rac');
        const isReserved = availLower.includes('reserved') || availLower.includes('resv');
        const isNotAvail = availLower.includes('not') || 
                           availLower.includes('regret') || 
                           (availLower.includes('wl') && !isAvbl);

        const statusFromFlag = refreshed ? refreshed.is_avail : (
            leg.seat_avail_ui && compareClasses(leg.seat_avail_ui.seat_class, cls)
                ? leg.seat_avail_ui.is_avail
                : null
        );

        let statusClass = 'unknown';
        if (statusFromFlag === true) {
            statusClass = 'available';
        } else if (statusFromFlag === false) {
            statusClass = 'not-available';
        } else if (isNotAvail) {
            statusClass = 'not-available';
        } else if (isRac || isAvbl) {
            statusClass = 'available';
        } else if (isReserved) {
            statusClass = 'reserved';
        }

        return { uniqueKey, displayCls, displayQuota, availInfo, cost, statusClass, busy };
    };

    // Always resolve to a valid class — handles stale state on first render
    const selectedIdx = availableClasses.findIndex(c => compareClasses(c, selectedClass));
    const effectiveClass = selectedIdx !== -1 ? selectedClass : availableClasses[0];
    const effectiveIdx = selectedIdx !== -1 ? selectedIdx : 0;
    const activeData = effectiveClass ? getClassData(effectiveClass, effectiveIdx) : null;
    const activeUniqueKey = effectiveClass ? `${leg.number}_${effectiveIdx}` : null;
    const isActiveRefreshing = activeUniqueKey !== null && isClassBusy(effectiveClass);

    return (
        <div className="seat-availability-swiper-container">
            <AvailabilityNote seatAvail={leg.seat_avail_ui} trainNum={leg.number} />
            <Swiper
                spaceBetween={12}
                slidesPerView="auto"
                freeMode={true}
                navigation={true}
                observer={true}
                observeParents={true}
                modules={[FreeMode, Navigation, Keyboard, Mousewheel]}
                keyboard={{ enabled: true }}
                mousewheel={{ forceToAxis: true }}
                className="seat-availability-swiper"
            >
                {availableClasses.map((cls, idx) => {
                    const data = getClassData(cls, idx);
                    const isSelected = compareClasses(effectiveClass, cls);

                    return (
                        <SwiperSlide key={idx} style={{ width: 'auto' }}>
                            <div 
                                className={`seat-class-box ${data.statusClass} ${isSelected ? 'selected-seat-class' : ''}`}
                                onClick={() => { setSelectedClass(cls); userPickedRef.current = true; if (onClassChange) onClassChange(cls); }}
                            >
                                <div className="seat-card-grid">
                                    <div className="seat-grid-top-left">{data.displayCls}</div>
                                    <div className="seat-grid-top-right">({data.displayQuota})</div>
                                    <div className="seat-grid-bottom-left">
                                        {data.busy && !data.availInfo ? (
                                            <span className="skeleton-bar" style={{ display: 'inline-block', width: 56 }} aria-label="Checking availability" />
                                        ) : (
                                            <span className={`seat-status-text-styled ${data.statusClass}`}>
                                                {data.availInfo || 'N/A'}
                                            </span>
                                        )}
                                    </div>
                                    <div className="seat-grid-bottom-right">
                                        {data.cost ? `₹${data.cost}` : '--'}
                                    </div>
                                </div>

                                <div
                                    className={`seat-refresh-layer ${data.busy ? 'refreshing' : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedClass(cls);
                                        userPickedRef.current = true; if (onClassChange) onClassChange(cls);
                                        handleRefresh(leg, cls);
                                    }}
                                >
                                    {data.busy ? (
                                        <SeatSpinner />
                                    ) : (
                                        <SeatRefreshIcon />
                                    )}
                                </div>
                            </div>
                        </SwiperSlide>
                    );
                })}
            </Swiper>
            <div className="seat-availability-detail-text">
                {isActiveRefreshing ? (
                    <div className="seat-detail-skeleton">
                        <div className="skeleton-bar" style={{ width: 90 }} />
                        <div className="skeleton-bar" style={{ width: 50 }} />
                    </div>
                ) : activeData ? (
                    <div></div>
                ) : <div></div>}
                {actionButton && (
                    <div className="seat-detail-right">
                        {actionButton}
                    </div>
                )}
            </div>
        </div>
    );
};

const FlightIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2 20H18" stroke="#6366F1" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M3.82496 12.1661C3.54996 11.9661 3.29996 11.7161 2.99997 10.8411C2.9186 10.6241 2.61108 9.53619 2.34997 8.54109C2.12972 7.7017 1.93346 6.93555 2.02497 6.74109C2.09998 6.54109 2.19996 6.39109 2.52496 6.19109C2.72496 6.06802 3.74996 5.81609 3.94996 5.76609C4.14996 5.71609 4.42495 5.69109 4.64996 5.76609C5.07496 5.84109 5.94996 7.11609 6.17496 7.26609C6.27495 7.36609 6.59996 7.657 6.97496 7.69109C7.24996 7.71609 7.52497 7.64109 7.82498 7.51609C8.09997 7.40151 13.525 4.76609 14.025 4.54109C18.1 2.84109 21.06 5.63609 21.51 6.23609C21.975 6.81609 22.075 6.99109 21.95 7.49109C21.7884 8.01609 21.35 8.11609 21.1 8.19109C20.85 8.26609 17.4 9.19109 16.05 9.56609C15.7551 9.6621 15.6111 9.85492 15.575 9.89109C15.4 10.1411 14.605 11.8411 14.38 12.2161C14.225 12.6161 13.8 13.1161 13.25 13.3161C12.675 13.5161 11.675 13.7411 11.45 13.8161C11.225 13.8911 10.7 14.0411 10.525 13.9911C10.3 13.9411 10.085 13.7161 10.185 13.3661C10.285 13.0161 10.475 12.0411 10.5 11.8911C10.525 11.7411 10.775 11.1161 10.5 11.0911C10.45 11.0161 9.92497 11.2411 9.14997 11.4161C8.57419 11.5782 7.97119 11.7386 7.54996 11.8411C5.92496 12.3161 5.0449 12.4411 4.84996 12.4411C4.47496 12.4411 4.19996 12.3911 3.82496 12.1661Z" stroke="#6366F1" stroke-width="2.2" />
    </svg>
);
const TrainIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7 6.99805V3.09766C7 2.54537 7.44772 2.09766 8 2.09766C8.55228 2.09766 9 2.54537 9 3.09766V6.99805L9.01465 7.29395C9.08279 7.9808 9.38657 8.6268 9.87891 9.11914C10.4415 9.68163 11.2044 9.99805 12 9.99805L12.2969 9.9834C12.9834 9.91516 13.6289 9.61123 14.1211 9.11914C14.6134 8.6268 14.9172 7.9808 14.9854 7.29395L15 6.99805V3.09766C15 2.54537 15.4477 2.09766 16 2.09766C16.5523 2.09766 17 2.54537 17 3.09766V6.99805L16.9941 7.24512C16.9329 8.48111 16.4143 9.65401 15.5352 10.5332C14.6561 11.4121 13.4837 11.9299 12.248 11.9912L12 11.998C10.674 11.998 9.40251 11.4708 8.46484 10.5332C7.58565 9.65401 7.06706 8.48111 7.00586 7.24512L7 6.99805Z" fill="#FF670A" stroke="#FF670A" stroke-width="0.5" />
        <path d="M7.29296 13.2927C7.65908 12.9266 8.23808 12.904 8.63085 13.2244L8.70703 13.2927L9.70703 14.2927L9.77539 14.3689C10.0957 14.7617 10.0731 15.3407 9.70703 15.7068C9.34091 16.0729 8.76191 16.0955 8.36914 15.7752L8.29296 15.7068L7.29296 14.7068L7.2246 14.6306C6.90425 14.2379 6.92685 13.6589 7.29296 13.2927Z" fill="#FF670A" stroke="#FF670A" stroke-width="0.5" />
        <path d="M15.3691 13.2244C15.7619 12.904 16.3409 12.9266 16.707 13.2927C17.0731 13.6589 17.0957 14.2379 16.7754 14.6306L16.707 14.7068L15.707 15.7068C15.3165 16.0973 14.6835 16.0973 14.293 15.7068C13.9024 15.3163 13.9024 14.6833 14.293 14.2927L15.293 13.2927L15.3691 13.2244Z" fill="#FF670A" stroke="#FF670A" stroke-width="0.5" />
        <path d="M18.9912 9.65332C18.9055 7.92303 18.181 6.28061 16.9502 5.0498C15.7194 3.819 14.077 3.09452 12.3467 3.00879L12 3C10.1435 3 8.36256 3.73705 7.0498 5.0498C5.73705 6.36256 5 8.14348 5 10V14C5 16.2477 6.75228 18 9 18H15C17.2477 18 19 16.2477 19 14V10L18.9912 9.65332ZM21 14C21 17.3523 18.3523 20 15 20H9C5.64772 20 3 17.3523 3 14V10C3 7.61305 3.94791 5.32357 5.63574 3.63574C7.32357 1.94791 9.61305 1 12 1C14.3869 1 16.6764 1.94791 18.3643 3.63574C20.0521 5.32357 21 7.61305 21 10V14Z" fill="#FF670A" stroke="#FF670A" stroke-width="0.5" />
        <path d="M7.16797 18.4454C7.47432 17.9859 8.09516 17.8617 8.55469 18.1681C9.01421 18.4744 9.13838 19.0952 8.83203 19.5548L6.83203 22.5548C6.52568 23.0143 5.90484 23.1385 5.44531 22.8321C4.98578 22.5258 4.86161 21.9049 5.16797 21.4454L7.16797 18.4454Z" fill="#FF670A" stroke="#FF670A" stroke-width="0.5" />
        <path d="M15.4456 18.1681C15.8765 17.8808 16.4495 17.972 16.7718 18.3634L16.8323 18.4455L18.8323 21.4455L18.8851 21.5333C19.1221 21.9813 18.9858 22.545 18.555 22.8322C18.124 23.1195 17.5511 23.0283 17.2288 22.6369L17.1683 22.5548L15.1683 19.5548L15.1155 19.4669C14.8785 19.019 15.0148 18.4553 15.4456 18.1681Z" fill="#FF670A" stroke="#FF670A" stroke-width="0.5" />
    </svg>
);



const LayoverIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 19C8 17.8954 7.10457 17 6 17C4.89543 17 4 17.8954 4 19C4 20.1046 4.89543 21 6 21C7.10457 21 8 20.1046 8 19ZM10 19C10 21.2091 8.20914 23 6 23C3.79086 23 2 21.2091 2 19C2 16.7909 3.79086 15 6 15C8.20914 15 10 16.7909 10 19Z" fill="#475569" />
        <path d="M20 15.5C20 14.837 19.7364 14.2013 19.2676 13.7324C18.8573 13.3222 18.3194 13.0695 17.7471 13.0127L17.5 13H6.5C5.30653 13 4.16227 12.5256 3.31836 11.6816C2.52708 10.8904 2.06093 9.83506 2.00586 8.72266L2 8.5C2 7.30653 2.47445 6.16227 3.31836 5.31836C4.16227 4.47445 5.30653 4 6.5 4H15C15.5523 4 16 4.44772 16 5C16 5.55228 15.5523 6 15 6H6.5C5.83696 6 5.20126 6.26358 4.73242 6.73242C4.26358 7.20126 4 7.83696 4 8.5L4.0127 8.74707C4.06952 9.31937 4.32219 9.85735 4.73242 10.2676C5.20126 10.7364 5.83696 11 6.5 11H17.5L17.7227 11.0059C18.8351 11.0609 19.8904 11.5271 20.6816 12.3184C21.5256 13.1623 22 14.3065 22 15.5C22 16.6935 21.5256 17.8377 20.6816 18.6816C19.8904 19.4729 18.8351 19.9391 17.7227 19.9941L17.5 20H9C8.44772 20 8 19.5523 8 19C8 18.4477 8.44772 18 9 18H17.5L17.7471 17.9873C18.3194 17.9305 18.8573 17.6778 19.2676 17.2676C19.7364 16.7987 20 16.163 20 15.5Z" fill="#475569" />
        <path d="M20 5C20 3.89543 19.1046 3 18 3C16.8954 3 16 3.89543 16 5C16 6.10457 16.8954 7 18 7C19.1046 7 20 6.10457 20 5ZM22 5C22 7.20914 20.2091 9 18 9C15.7909 9 14 7.20914 14 5C14 2.79086 15.7909 1 18 1C20.2091 1 22 2.79086 22 5Z" fill="#475569" />
    </svg>
);

const ReachIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 15L15 17V21H21V17L18 15ZM23 21C23 21.5304 22.7891 22.039 22.4141 22.4141C22.039 22.7891 21.5304 23 21 23H15C14.4696 23 13.961 22.7891 13.5859 22.4141C13.2109 22.039 13 21.5304 13 21V17C13 16.6709 13.0811 16.3468 13.2363 16.0566C13.3916 15.7664 13.6159 15.5186 13.8896 15.3359H13.8906L16.8896 13.3369V13.3359C17.2183 13.1167 17.6049 13 18 13C18.3951 13 18.7817 13.1167 19.1104 13.3359L19.1094 13.3369L22.1094 15.3359H22.1104L22.21 15.4082C22.4385 15.5819 22.6278 15.8027 22.7637 16.0566C22.9189 16.3468 23 16.6709 23 17V21Z" fill="#16A34A" />
        <path d="M17 10C17 8.14348 16.2629 6.36256 14.9502 5.0498C13.7194 3.819 12.077 3.09452 10.3467 3.00879L10 3C8.14348 3 6.36256 3.73705 5.0498 5.0498C3.73705 6.36256 3 8.14348 3 10C3 12.1593 4.21679 14.4871 5.79785 16.5645C7.32796 18.5748 9.08458 20.2015 10.0039 20.999C10.5539 21.0011 10.9994 21.4474 11 21.998C11.0005 22.5157 10.6076 22.9423 10.1035 22.9941L10.001 22.999L9.83887 22.9922C9.5168 22.9663 9.20594 22.8628 8.93262 22.6904L8.79883 22.5986C8.78059 22.5849 8.76237 22.5706 8.74512 22.5557C7.78983 21.7308 5.88248 19.978 4.20605 17.7754C2.55262 15.603 1 12.8337 1 10C1 7.61305 1.94791 5.32357 3.63574 3.63574C5.32357 1.94791 7.61305 1 10 1C12.3869 1 14.6764 1.94791 16.3643 3.63574C18.0521 5.32357 19 7.61305 19 10C19 10.5523 18.5523 11 18 11C17.4477 11 17 10.5523 17 10Z" fill="#16A34A" />
        <path d="M17 22V19C17 18.4477 17.4477 18 18 18C18.5523 18 19 18.4477 19 19V22C19 22.5523 18.5523 23 18 23C17.4477 23 17 22.5523 17 22Z" fill="#16A34A" />
        <path d="M12 10C12 8.89543 11.1046 8 10 8C8.89543 8 8 8.89543 8 10C8 11.1046 8.89543 12 10 12C11.1046 12 12 11.1046 12 10ZM14 10C14 12.2091 12.2091 14 10 14C7.79086 14 6 12.2091 6 10C6 7.79086 7.79086 6 10 6C12.2091 6 14 7.79086 14 10Z" fill="#16A34A" />
    </svg>
);

const FlightMultiResultCard = ({ card, onViewMap, onShare, onBookClick, onBookBus, onShowError }) => {

    const [isExpanded, setIsExpanded] = useState(false);
    // Seat class the user picked per train leg (keyed by train number), for the price
    const [selectedClassByLeg, setSelectedClassByLeg] = useState({});

    // Live availability class checks of each train leg (collapsed footer loader)
    const trainLegs = (card.legs || []).filter(l => l.type === 'train' && l.train_segment);
    const primaryLegKeys = trainLegs.map(l => segAvailKeyOf(l.train_segment));
    const legStatuses = useAvailStatuses(primaryLegKeys);
    const legBusy = (i) => !!primaryLegKeys[i] && isBusyStatus(legStatuses[primaryLegKeys[i]]);
    const legAnswered = (l) => !!l.avail_by_class && Object.keys(l.avail_by_class).length > 0;
    // "Checking seats…" only until every train leg has its first answer (round one).
    // After that the real text shows ("Seats not available" / "Seats partially
    // available" / "Seats available") and updates as later classes come in.
    const isCheckingSeats = trainLegs.some((l, i) => legBusy(i) && !legAnswered(l));
    const isRefiningSeats = !isCheckingSeats && trainLegs.some((l, i) => legBusy(i));
    const [isBookingLoading, setIsBookingLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { openLoginModal } = useAuth();

    const handleBookFlight = async (legOverride = null) => {
        // Get Firebase user token
        const auth = getAuth();
        const user = auth.currentUser;

        // Open the booking tab now, while we still have the click (browsers block
        // popups opened after an await). It shows a loader until the fare check is done.
        const tabHandle = user ? openBookingTab() : null;

        // Save source and query to localStorage for persistence across reloads/logouts
        localStorage.setItem("booking_source", "search");
        localStorage.setItem("booking_search_query", location.search);

        // Pre-check removed to allow API to handle auth (consistent with other cards)

        let userToken = null;

        if (onBookClick) onBookClick();

        // Token will be fetched inside the try block

        // Use override leg if provided, otherwise find first flight leg
        const flightLeg = legOverride || card.legs?.find(l => l.type === 'flight');

        const trace_id = flightLeg?.trace_id || card.trace_id;
        const result_idx = flightLeg?.result_idx || card.result_idx;
        const is_lcc = flightLeg?.is_lcc !== undefined ? flightLeg.is_lcc : card.is_lcc;
        const valid_till_ts = flightLeg?.valid_till_ts || card.valid_till_ts;

        setIsBookingLoading(true);
        try {
            let userToken = null;
            if (user) {
                userToken = await user.getIdToken();
            }

            // Call verify API first
            const fareQuoteResponse = await fetchFareQuote(trace_id, result_idx, userToken);

            if (onBookClick) onBookClick();

            const params = new URLSearchParams();
            if (trace_id) params.append("trace_id", trace_id);
            if (result_idx !== undefined && result_idx !== null)
                params.append("result_idx", result_idx);
            if (is_lcc !== undefined && is_lcc !== null)
                params.append("is_lcc", is_lcc);
            if (valid_till_ts !== undefined && valid_till_ts !== null)
                params.append("valid_till_ts", valid_till_ts);

            const url = `/book-flight?${params.toString()}`;

            deliverBookingTab(tabHandle, navigate, url, {
                state: {
                    preFetchedQuote: fareQuoteResponse,
                    trace_id,
                    result_idx,
                    is_lcc,
                    valid_till_ts,
                    baggage: flightLeg?.baggage || card.baggage,
                    userToken,
                    source: "search",
                    searchQuery: location.search,
                    flightDetails: {
                        departure: card.origin,
                        arrival: card.destination,
                        duration: card.totalDuration,
                        stops: card.stops,
                        price: card.price,
                        currency: card.currency || '₹',
                        tag: card.tags?.[0] || '',
                        modes: [card.mode || 'flight'],
                    },
                },
            });
        } catch (error) {
            console.error("Booking failed:", error);
            cancelBookingTab(tabHandle);
            setIsBookingLoading(false);

            // Handle unauthorized error - show login modal
            const isAuthError =
                error.status === 401 ||
                error.response?.status === 401 ||
                error.message?.toLowerCase().includes("unauthorized") ||
                error.message?.toLowerCase().includes("login required") ||
                error.message?.toLowerCase().includes("auth verifier");

            if (isAuthError) {
                openLoginModal(() => handleBookFlight(legOverride));
            } else if (error instanceof FallbackAvailableError) {
                if (onShowError) onShowError(error);
            } else {
                if (onShowError) onShowError({
                    message: error.message || "Failed to load booking details. Please try again.",
                    operationName: "Booking Error"
                });
            }
        }
    };

    // Navigate to bus booking page from expanded view.
    // Delegates to onBookBus (SearchForm handler) so raw_segment and flat fields
    // (result_idx, inventory_type, origin_name, dest_name) are passed correctly.
    const handleBookBus = (leg) => {
        if (onBookBus) onBookBus(leg, card);
    };

    // Manual refresh of one seat class. Runs immediately and in parallel with any
    // other cell; the result lands in the store so sorting sees it too.
    const handleRefresh = (leg, cls) => {
        if (leg?.train_segment) requestAvailability(leg.train_segment, cls);
    };


    const mode = card.mode || 'flight';
    const isMixedBF = mode === 'mixed-bf';
    const isMixedTB = mode === 'mixed-tb';
    const isMixedBT = mode === 'mixed-bt';
    const isFlight = mode === 'flight';
    const isBus = mode === 'bus' || isMixedBF || isMixedTB || isMixedBT;
    const formatPrice = (amount) => {
        if (amount === null || amount === undefined) return null;
        const val = parseFloat(typeof amount === 'string' ? amount.replace(/,/g, '') : amount);
        if (isNaN(val) || !amount) return null;
        return val.toLocaleString('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        });
    };

    // Cab selections: origin/dest cabs + per-layover cabs (keyed by leg index)
    const [cabSel, setCabSel] = useState(() => {
        const init = {
            origin: defaultCabCategory(card.originCab),
            dest: defaultCabCategory(card.destCab)
        };
        (card.legs || []).forEach((leg, i) => {
            if (leg.layoverCab) init[`l${i}`] = defaultCabCategory(leg.layoverCab);
        });
        return init;
    });
    const cabTotal =
        selectedCabPrice(card.originCab, cabSel.origin) +
        selectedCabPrice(card.destCab, cabSel.dest) +
        (card.legs || []).reduce((s, leg, i) =>
            s + (leg.layoverCab ? selectedCabPrice(leg.layoverCab, cabSel[`l${i}`]) : 0), 0);

    const displayPrice = (() => {
        let total = 0;
        let foundAny = false;

        card.legs?.forEach((leg) => {
            if (leg.type === 'layover') return;

            // A class the user picked on this leg shows its live fare
            const pickedCls = selectedClassByLeg[leg.number];
            if (pickedCls) {
                const pickedFare = toFareNum(availEntryOf(leg.avail_by_class, pickedCls)?.fare ?? leg.seat_costs?.[pickedCls]);
                if (pickedFare !== null) {
                    total += pickedFare;
                    foundAny = true;
                    return;
                }
            }

            if (leg.fare) {
                total += parseFloat(typeof leg.fare === 'string' ? leg.fare.replace(/,/g, '') : leg.fare);
                foundAny = true;
            }
        });

        if (foundAny) return formatPrice(total);
        const base = parseFloat(String(card.price || '').replace(/[^0-9.]/g, ''));
        if (!isNaN(base)) return formatPrice(base);
        return card.price || '-';
    })();

    return (
        <div className={`multi-leg-card-container ${isExpanded ? 'expanded' : ''}`}>
            {/* Main Collapsed Card */}
            <div className="main-card-view" onClick={() => setIsExpanded(!isExpanded)}>
                <div className="card-header">
                    <div className="header-left" style={{ overflow: 'hidden', flex: 1, minWidth: 0 }}>
                        {card.modeOperators?.length >= 2 ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden', width: '100%' }}>
                                {card.modeOperators.map((m, i) => (
                                    <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                                        {i > 0 && <span style={{ color: '#94A3B8', fontSize: 14, flexShrink: 0 }}>›</span>}
                                        {m.type === 'bus'    && <Bus size={18} color="#16A34A" strokeWidth={2.5} style={{ flexShrink: 0 }} />}
                                        {m.type === 'flight' && <FlightIcon style={{ flexShrink: 0 }} />}
                                        {m.type === 'train'  && <TrainIcon style={{ flexShrink: 0 }} />}
                                        <span className="airline-numbers" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</span>
                                    </span>
                                ))}
                            </span>
                        ) : (
                            <>
                                {isFlight ? <FlightIcon /> : isBus ? <Bus size={24} color="#16A34A" strokeWidth={2.5} /> : <TrainIcon />}
                                <span className="airline-numbers">{card.airlineInfo}</span>
                            </>
                        )}
                    </div>
                    <div className="card-tags">
                        <button
                            className="result-card-map-btn icon-only"
                            onClick={(e) => {
                                e.stopPropagation();
                                onViewMap();
                            }}
                            title="View Route on Map"
                        >
                            <FaRoute size={16} />
                        </button>
                        <button
                            className="result-card-map-btn share-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                onShare();
                            }}
                            title="Share Route"
                            style={{ width: 'auto', padding: '0 8px', gap: '4px' }}
                        >
                            <Share2 size={14} />
                            <span>Share</span>
                        </button>
                        {card.tags && card.tags.length > 0 && card.tags.map((tag, idx) => (
                            <span key={idx} className={`card-tag ${tag.toLowerCase()}`}>
                                {tag}
                            </span>
                        ))}
                    </div>
                </div>

                <div className="card-body">
                    <div className="journey-summary">
                        <div className="station-info">
                            <span className="station-sub">{card.origin.name}</span>
                            <span className="station-main">{(isBus ? firstWord(card.origin.name) : card.origin.code)}, {card.origin.time}</span>
                            <span className="date-text">{card.origin.date}</span>
                        </div>

                        <div className="visual-separator">
                            <div className="dashed-line"></div>
                            <div className="duration-info-box">
                                <span className="duration-label">{card.totalDuration}</span>
                                <span className="stops-label">
                                    {(card.stops || 0) === 0
                                        ? '0 change stop'
                                        : (isFlight || isBus
                                            ? `${card.stops} change ${card.stops === 1 ? 'stop' : 'stops'}`
                                            : `${card.stops} change ${card.stops === 1 ? 'stop' : 'stops'}`

                                        )
                                    }
                                </span>
                            </div>
                        </div>

                        <div className="station-info right">
                            <span className="station-sub">{card.destination.name}</span>
                            <span className="station-main">{(isBus ? firstWord(card.destination.name) : card.destination.code)}, {card.destination.time}</span>
                            <span className="date-text">{card.destination.date}</span>
                        </div>
                    </div>

                    <div className="price-fare-section">
                        <div className="fare-section-left">
                            {(() => {
                                const mode = card.mode || 'flight';
                                const isTrain = mode === 'train';
                                const isMultiLeg = (card.legs?.filter(l => l.type !== 'layover').length > 1);
                                const seatUI = card.legs?.find(l => l.type === 'train')?.seat_avail_ui || card.seat_avail_ui;
                                const isInvalid = isTrain && (!seatUI || (seatUI.avl === null && seatUI.is_avail === false));

                                // Bus-aware seatStatus: bus legs are always available — derive from seat_avail_ui flat format
                                // if the parent hasn't populated card.seatStatus yet
                                const busSeatStatus = (() => {
                                    if (!isBus) return card.seatStatus;
                                    if (card.seatStatus) return card.seatStatus;
                                    // Try bus leg's seat_avail_ui (flat format)
                                    const busLeg = card.legs?.find(l => l.type === 'bus');
                                    const busSeatUI = busLeg?.seat_avail_ui || card.seat_avail_ui;
                                    if (!busSeatUI) return "Seats available"; // bus is always available
                                    if (busSeatUI.avl !== undefined) {
                                        const avl = busSeatUI.avl;
                                        const count = parseInt(avl, 10);
                                        return isNaN(count) ? "Seats available" : `${count} Seats Available`;
                                    }
                                    // legacy nested fallback
                                    const generalEntry = busSeatUI['General'];
                                    if (generalEntry) {
                                        const count = generalEntry.available;
                                        return typeof count === 'number' ? `${count} Seats Available` : "Seats available";
                                    }
                                    return "Seats available"; // bus is always available
                                })();
                                const effectiveSeatStatus = isBus ? busSeatStatus : card.seatStatus;

                                // Live availability still loading for a train leg
                                if (isCheckingSeats) return <SeatCheckingStatus />;

                                // Logic for Trains
                                if (isTrain) {
                                    // For multi-leg, trust the aggregate seatStatus. For single-leg, keep strict hiding.
                                    if (isMultiLeg) {
                                        if (!effectiveSeatStatus) return null;
                                    } else {
                                        if (isInvalid || !effectiveSeatStatus) return null;
                                    }
                                }

                                if (effectiveSeatStatus) {
                                    return (
                                        <div className="seat-status-container" style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            marginBottom: '4px'
                                        }}>
                                            <div className="tooltip-container">
                                                <div className="tooltip-trigger">
                                                    <Info size={18} color={
                                                        isBus
                                                            ? (effectiveSeatStatus && effectiveSeatStatus !== 'Sold out' ? "#16A34A" : "#DC2626")
                                                            : effectiveSeatStatus === "Seats available" ? "#16A34A" :
                                                                effectiveSeatStatus === "Seats partially available" ? "#CA8A04" : "#DC2626"
                                                    } />
                                                    <span className="tooltip-text">
                                                        {isBus
                                                            ? effectiveSeatStatus
                                                            : effectiveSeatStatus === "Seats available"
                                                                ? "Seats available on all segments of this route"
                                                                : effectiveSeatStatus === "Seats partially available"
                                                                    ? "Seats are available on some segments of this route"
                                                                    : "Seats are not available on this route"
                                                        }
                                                    </span>
                                                </div>
                                            </div>

                                            <span className="seat-status-text" style={{
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: isBus
                                                    ? (effectiveSeatStatus && effectiveSeatStatus !== 'Sold out' ? "#16A34A" : "#DC2626")
                                                    : effectiveSeatStatus === "Seats available" ? "#16A34A" :
                                                        effectiveSeatStatus === "Seats partially available" ? "#CA8A04" : "#DC2626",
                                                fontFamily: "'Inter', sans-serif"
                                            }}>
                                                {effectiveSeatStatus}
                                            </span>
                                            {isRefiningSeats && <SeatRefiningHint />}
                                        </div>
                                    );
                                } else if (!isTrain && card.fareOptions && card.fareOptions.length > 0) {
                                    return (
                                        <div className="fare-pills-swiper-container">
                                            <Swiper
                                                spaceBetween={8}
                                                slidesPerView="auto"
                                                freeMode={true}
                                                navigation={true}
                                                observer={true}
                                                observeParents={true}
                                                modules={[FreeMode, Navigation, Keyboard, Mousewheel]}
                                                keyboard={{ enabled: true }}
                                                mousewheel={{ forceToAxis: true }}
                                                className="fare-pills-swiper"
                                            >
                                                {card.fareOptions.map((option, idx) => (
                                                    <SwiperSlide key={idx} style={{ width: 'auto' }}>
                                                        <div className="result-card-fare-pill">
                                                            <div className="result-card-fare-label-row">
                                                                <span className="result-card-class">{option.class}</span>
                                                                <span className="result-card-avail">{option.avail}</span>
                                                            </div>
                                                            <div className="result-card-tatkal-info">
                                                                <span className="result-card-quota-label">Total price</span>
                                                                <span className="result-card-fare-price">{option.price}</span>
                                                            </div>
                                                        </div>
                                                    </SwiperSlide>
                                                ))}
                                            </Swiper>
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                        </div>


                        <div className="action-area">
                            {isMixedBF ? (() => {
                                const busLeg    = card.legs?.find(l => l.type === 'bus');
                                const flightLeg = card.legs?.find(l => l.type === 'flight');
                                // Bus: first word of city name TitleCased (e.g. "Pune" from "pune satara rd")
                                const fw = (s) => s ? toTitleCase(s.trim().split(/\s+/)[0]) : '';
                                const busLabel    = busLeg
                                    ? `Book ${fw(busLeg.origin?.name)} → ${fw(busLeg.destination?.name)} Bus`
                                    : 'Book Bus';
                                // Flight: IATA codes (e.g. "BOM → BLR")
                                const flightLabel = flightLeg
                                    ? `Book ${flightLeg.origin?.code || fw(flightLeg.origin?.name)} → ${flightLeg.destination?.code || fw(flightLeg.destination?.name)} Flight`
                                    : 'Book Flight';

                                // Render buttons in itinerary order (match the legs array order)
                                const busIdx    = card.legs?.findIndex(l => l.type === 'bus') ?? 1;
                                const flightIdx = card.legs?.findIndex(l => l.type === 'flight') ?? 0;
                                const flightFirst = flightIdx < busIdx;

                                const flightBtn = (
                                    <div className="mixed-bf-btn-group">
                                        <button className="book-now-btn flight" disabled={isBookingLoading} onClick={(e) => { e.stopPropagation(); handleBookFlight(); }}>
                                            {isBookingLoading ? 'Processing...' : flightLabel}
                                        </button>
                                        {card.flat_off_flight_promo_desc && (
                                            <div className="flight-deal-banner-row">
                                                <span className="flight-deal-badge"><span className="flight-deal-dot"></span>{card.flat_off_flight_promo_desc}</span>
                                            </div>
                                        )}
                                    </div>
                                );
                                const busBtn = (
                                    <div className="mixed-bf-btn-group">
                                        <button className="book-now-btn bus" onClick={(e) => { e.stopPropagation(); if (busLeg && onBookBus) onBookBus(busLeg, card); }}>
                                            {busLabel}
                                        </button>
                                        {getBusPromoDesc(card) && (
                                            <div className="flight-deal-banner-row">
                                                <span className="flight-deal-badge"><span className="flight-deal-dot"></span>{getBusPromoDesc(card)}</span>
                                            </div>
                                        )}
                                    </div>
                                );
                                return (
                                    <div className="mixed-bf-buttons">
                                        {flightFirst ? flightBtn : busBtn}
                                        {flightFirst ? busBtn : flightBtn}
                                    </div>
                                );
                            })() : (isMixedTB || isMixedBT) ? (() => {
                                const trainLeg = card.legs?.find(l => l.type === 'train');
                                const busLeg   = card.legs?.find(l => l.type === 'bus');
                                const fw = (s) => s ? toTitleCase(s.trim().split(/\s+/)[0]) : '';

                                // Train button: station codes if available (e.g. "PUNE → BSL")
                                const trainLabel = trainLeg
                                    ? `Book ${trainLeg.origin?.code || fw(trainLeg.origin?.name)} → ${trainLeg.destination?.code || fw(trainLeg.destination?.name)} Train (IRCTC)`
                                    : 'Book Train (IRCTC)';
                                // Bus button: first word of city names
                                const busLabel = busLeg
                                    ? `Book ${fw(busLeg.origin?.name)} → ${fw(busLeg.destination?.name)} Bus`
                                    : 'Book Bus';

                                // Render in itinerary order
                                const trainIdx = card.legs?.findIndex(l => l.type === 'train') ?? 0;
                                const busIdx   = card.legs?.findIndex(l => l.type === 'bus') ?? 1;
                                const trainFirst = trainIdx < busIdx;

                                const trainBtn = (
                                    <div className="mixed-bf-btn-group">
                                        <button
                                            className="book-now-btn train"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (trainLeg?.booking_link) {
                                                    window.open(trainLeg.booking_link, '_blank', 'noopener,noreferrer');
                                                }
                                            }}
                                        >
                                            {trainLabel} <ExternalLink size={13} style={{ marginLeft: '5px', verticalAlign: 'middle' }} />
                                        </button>
                                    </div>
                                );
                                const busBtn = (
                                    <div className="mixed-bf-btn-group">
                                        <button
                                            className="book-now-btn bus"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (busLeg && onBookBus) onBookBus(busLeg, card);
                                            }}
                                        >
                                            {busLabel}
                                        </button>
                                        {getBusPromoDesc(card) && (
                                            <div className="flight-deal-banner-row">
                                                <span className="flight-deal-badge"><span className="flight-deal-dot"></span>{getBusPromoDesc(card)}</span>
                                            </div>
                                        )}
                                    </div>
                                );
                                return (
                                    <div className="mixed-bf-buttons">
                                        {trainFirst ? trainBtn : busBtn}
                                        {trainFirst ? busBtn : trainBtn}
                                    </div>
                                );
                            })() : (
                                <button
                                    className={`book-now-btn ${mode}`}
                                    disabled={isBookingLoading}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (isFlight) {
                                            handleBookFlight();
                                        } else {
                                            const firstLegWithLink = card.legs.find(l => l.booking_link);
                                            if (firstLegWithLink?.booking_link) {
                                                window.open(firstLegWithLink.booking_link, '_blank', 'noopener,noreferrer');
                                            }
                                        }
                                    }}
                                >
                                    {isBookingLoading ? "Processing..." : (
                                        <>
                                            {mode === 'train'
                                                ? `Book Train${(card.stops || 0) > 0 ? 's' : ''} (IRCTC)`
                                                : `Book ${mode.charAt(0).toUpperCase() + mode.slice(1)}`
                                            }
                                            {mode === 'train' && <ExternalLink size={14} style={{ marginLeft: '6px' }} />}
                                        </>
                                    )}
                                </button>
                            )}

                            <button
                                className="card-map-text-link"
                                onClick={(e) => { e.stopPropagation(); onViewMap(); }}
                            >
                                <FaRoute size={13} style={{ flexShrink: 0 }} />
                                View on Map
                            </button>

                            <div className="final-price-row">
                                <span className="final-price">{displayPrice}</span>
                                <button className="expand-toggle-btn" onClick={() => setIsExpanded(!isExpanded)}>
                                    {isExpanded ? <ChevronUp size={23} /> : <ChevronDown size={23} />}
                                </button>
                            </div>
                        </div>
                    </div>

                    <AltBookingBanner items={(card.legs || []).filter(l => l.type === 'train').map(l => ({ trainNum: l.number, seatAvail: l.seat_avail_ui }))} style={{ marginTop: '-8px' }} />

                    {!isMixedBF && isFlight && card.flat_off_flight_promo_desc && (
                        <div className="flight-deal-banner-row">
                            <span className="flight-deal-badge"><span className="flight-deal-dot"></span>{card.flat_off_flight_promo_desc}</span>
                        </div>
                    )}
                    {!isMixedBF && !isMixedTB && !isMixedBT && isBus && getBusPromoDesc(card) && (
                        <div className="flight-deal-banner-row">
                            <span className="flight-deal-badge"><span className="flight-deal-dot"></span>{getBusPromoDesc(card)}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Expanded Details View */}
            {isExpanded && (
                <div className="expanded-details-view">
                    <div className="timeline-container">
                        {(card.stops || 0) === 0 ? (
                            <div className="timeline-segment">
                                <div className="sidebar-visual">
                                    <div className="node-icon-box">
                                        {card.legs[0]?.type === 'flight' && <img src="/icon4.svg" alt="Flight" style={{ width: 24, height: 24 }} />}
                                        {card.legs[0]?.type === 'train' && <img src="/icon1.svg" alt="Train" style={{ width: 24, height: 24 }} />}
                                        {card.legs[0]?.type === 'bus' && <Bus size={20} color="#16A34A" />}
                                    </div>
                                </div>
                                <div className="leg-details-container" style={{ flex: 1 }}>
                                    <div className={`expanded-leg-card ${card.legs[0]?.type}-type`}>
                                        <div className="leg-upper">
                                            <span className="leg-title">
                                                {card.legs[0]?.number}{card.legs[0]?.name ? `, ${card.legs[0].name}` : ''}
                                            </span>
                                            <span className="leg-type-tag">
                                                {card.legs[0]?.type?.charAt(0).toUpperCase() + card.legs[0]?.type?.slice(1)}
                                            </span>
                                        </div>

                                        <div className="leg-middle">
                                            <div className="leg-stations">
                                                <div className="leg-station">
                                                    <span className="s-sub">{card.legs[0]?.origin?.name}</span>
                                                    <span className="s-main">{getLegStationName(card.legs[0], 'origin') || card.legs[0]?.origin?.code}, {card.legs[0]?.origin?.time}</span>
                                                    <span className="s-date">{card.legs[0]?.origin?.date}</span>
                                                </div>
                                                <div className="leg-visual">
                                                    <span className="d-text">{card.legs[0]?.duration}</span>
                                                    <div className="d-line"></div>
                                                </div>
                                                <div className="leg-station right">
                                                    <span className="s-sub">{card.legs[0]?.destination?.name}</span>
                                                    <span className="s-main">{getLegStationName(card.legs[0], 'destination') || card.legs[0]?.destination?.code}, {card.legs[0]?.destination?.time}</span>
                                                    <span className="s-date">{card.legs[0]?.destination?.date}</span>
                                                </div>
                                            </div>
                                            {card.legs[0]?.type === 'train' && (
                                                <SeatAvailabilityGrid
                                                    leg={card.legs[0]}
                                                    handleRefresh={handleRefresh}
                                                    onClassChange={(cls) => setSelectedClassByLeg(prev => ({ ...prev, [card.legs[0].number]: cls }))}
                                                    actionButton={
                                                        <button
                                                            className={`book-leg-btn ${card.legs[0]?.type}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (card.legs[0]?.booking_link) {
                                                                    window.open(card.legs[0].booking_link, '_blank', 'noopener,noreferrer');
                                                                }
                                                            }}
                                                        >
                                                            Book on IRCTC
                                                        </button>
                                                    }
                                                />
                                            )}
                                        </div>
                                        {card.legs[0]?.type !== 'train' && (
                                            <div className="leg-footer">
                                                <div className="leg-price-container">
                                                    {(card.legs[0]?.type === 'flight' || card.legs[0]?.type === 'bus') && card.legs[0]?.price && (
                                                        <span className="leg-price-val">{card.legs[0].price}</span>
                                                    )}
                                                </div>
                                                {card.legs[0]?.type === 'flight' ? (
                                                    <div className="leg-button-wrapper">
                                                        <button
                                                            className={`book-leg-btn ${card.legs[0]?.type}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleBookFlight(card.legs[0]);
                                                            }}
                                                        >
                                                            Book Flight
                                                        </button>
                                                        {card.flat_off_flight_promo_desc && (
                                                            <span className="flight-deal-badge">
                                                                <span className="flight-deal-dot"></span>
                                                                {card.flat_off_flight_promo_desc}
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : card.legs[0]?.type === 'bus' ? (
                                                    <div className="leg-button-wrapper">
                                                        <button
                                                            className="book-leg-btn bus"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleBookBus(card.legs[0]);
                                                            }}
                                                        >
                                                            Book Bus
                                                        </button>
                                                        {getBusPromoDesc(card) && (
                                                            <span className="flight-deal-badge"><span className="flight-deal-dot"></span>{getBusPromoDesc(card)}</span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <button
                                                        className={`book-leg-btn ${card.legs[0]?.type}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (card.legs[0]?.booking_link) {
                                                                window.open(card.legs[0].booking_link, '_blank', 'noopener,noreferrer');
                                                            }
                                                        }}
                                                    >
                                                        {card.legs[0]?.type === 'train' ? 'Book on IRCTC' : `Book ${card.legs[0].type.charAt(0).toUpperCase() + card.legs[0].type.slice(1)}`}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        {(card.legs[0]?.footerText || card.explanation) && (
                                            <div className="leg-footer-msg">
                                                {[
                                                    ...(card.explanation ? [card.explanation] : []),
                                                    ...(card.legs[0]?.footerText ? card.legs[0].footerText.split('\n') : [])
                                                ].filter(line => line.trim() !== '').map((line, i) => <p key={i}>{line}</p>)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* First leg commute card — rendered BEFORE the static start block when present */}
                                {(card.legs[0]?.originCommentWeb || card.originCab) && (
                                    <div className="timeline-item">
                                        <div className="timeline-connector">
                                            <div className="node-icon">
                                                <img src="/icon3.svg" alt="Commute" style={{ width: 24, height: 24 }} />
                                            </div>
                                            <div className="vertical-line"></div>
                                        </div>
                                        <div className="leg-content">
                                            <div className="leg-card location-leg commute-leg">
                                                <div className="leg-header">
                                                    <span className="leg-title">{card.legs[0]?.originCommentWebTitle || 'Commute'}</span>
                                                    {card.originCab && (
                                                        <span className="cab-selected-price">{formatPrice(selectedCabPrice(card.originCab, cabSel.origin))}</span>
                                                    )}
                                                    <span className="leg-mode">Commute</span>
                                                </div>
                                                <div className="leg-body">
                                                    {card.legs[0]?.originCommentWeb && (
                                                        <p className="location-desc">{card.legs[0].originCommentWeb}</p>
                                                    )}
                                                    <CabOptions
                                                        cab={card.originCab}
                                                        selected={cabSel.origin}
                                                        onSelect={(c) => setCabSel(prev => ({ ...prev, origin: c }))}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {card.legs.map((leg, index) => (
                                    <React.Fragment key={index}>
                                        {/* Commute card for legs after the first — shown inline before the segment */}
                                        {index > 0 && leg.originCommentWeb && (
                                            <div className="timeline-item">
                                                <div className="timeline-connector">
                                                    <div className="node-icon">
                                                        <img src="/icon6.svg" alt="Commute" style={{ width: 24, height: 24 }} />
                                                    </div>
                                                    <div className="vertical-line"></div>
                                                </div>
                                                <div className="leg-content">
                                                    <div className="leg-card location-leg commute-leg">
                                                        <div className="leg-header">
                                                            <span className="leg-title">{leg.originCommentWebTitle || 'Commute'}</span>
                                                            <span className="leg-mode">Commute</span>
                                                        </div>
                                                        <div className="leg-body">
                                                            <p className="location-desc">{leg.originCommentWeb}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <div className="timeline-item">
                                            <div className="timeline-connector">
                                                <div className="node-icon">
                                                    {leg.type === 'flight' && <img src="/icon4.svg" alt="Flight" style={{ width: 24, height: 24 }} />}
                                                    {leg.type === 'train' && <img src="/icon1.svg" alt="Train" style={{ width: 24, height: 24 }} />}
                                                    {leg.type === 'bus' && <Bus size={20} color="#16A34A" strokeWidth={2.5} />}
                                                    {leg.type === 'layover' && <img src="/icon6.svg" alt="Layover" style={{ width: 24, height: 32 }} />}
                                                </div>
                                                {(index < card.legs.length - 1 || card.destCommentWeb) && <div className="vertical-line"></div>}
                                            </div>

                                            <div className="leg-content">
                                                {(leg.type === 'flight' || leg.type === 'train' || leg.type === 'bus') ? (
                                                    <div className={`leg-card ${leg.type}-leg`}>
                                                        <div className="leg-header">
                                                            <span className="leg-title">
                                                                {leg.type === 'bus' ? leg.number : `${leg.number}${leg.name ? `, ${leg.name}` : ''}`}
                                                            </span>
                                                            <span className="leg-mode">{leg.type.charAt(0).toUpperCase() + leg.type.slice(1)}</span>
                                                        </div>
                                                        <div className="leg-body">
                                                            <div className="leg-stations">
                                                                <div className="leg-station">
                                                                    <span className="s-sub">{leg.origin.name}</span>
                                                                    <span className="s-main">{getLegStationName(leg, 'origin') || leg.origin.code}, {leg.origin.time}</span>
                                                                    <span className="s-date">{leg.origin.date}</span>
                                                                </div>
                                                                <div className="leg-visual">
                                                                    <span className="d-text">{leg.duration}</span>
                                                                    <div className="d-line"></div>
                                                                </div>
                                                                <div className="leg-station right">
                                                                    <span className="s-sub">{leg.destination.name}</span>
                                                                    <span className="s-main">{getLegStationName(leg, 'destination') || leg.destination.code}, {leg.destination.time}</span>
                                                                    <span className="s-date">{leg.destination.date}</span>
                                                                </div>
                                                            </div>
                                                            {leg.type === 'train' && (
                                                                <SeatAvailabilityGrid
                                                                    leg={leg}
                                                                    handleRefresh={handleRefresh}
                                                                    onClassChange={(cls) => setSelectedClassByLeg(prev => ({ ...prev, [leg.number]: cls }))}
                                                                    actionButton={
                                                                        <button
                                                                            className={`book-leg-btn ${leg.type}`}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                if (leg.booking_link) {
                                                                                    window.open(leg.booking_link, '_blank', 'noopener,noreferrer');
                                                                                }
                                                                            }}
                                                                        >
                                                                            Book on IRCTC
                                                                        </button>
                                                                    }
                                                                />
                                                            )}
                                                            {leg.type !== 'train' && (
                                                                <div className="leg-footer">
                                                                    <div className="leg-price-container">
                                                                        {(leg.type === 'flight' || leg.type === 'bus') && leg.price && (
                                                                            <span className="leg-price-val">{leg.price}</span>
                                                                        )}
                                                                    </div>
                                                                    {leg.type === 'flight' ? (
                                                                        <div className="leg-button-wrapper">
                                                                            <button
                                                                                className={`book-leg-btn ${leg.type}`}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleBookFlight(leg);
                                                                                }}
                                                                            >
                                                                                Book Flight
                                                                            </button>
                                                                            {leg.type === 'flight' && card.flat_off_flight_promo_desc && (
                                                                                <span className="flight-deal-badge">
                                                                                    <span className="flight-deal-dot"></span>
                                                                                    {card.flat_off_flight_promo_desc}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    ) : leg.type === 'bus' ? (
                                                                        <div className="leg-button-wrapper">
                                                                            <button className="book-leg-btn bus" onClick={(e) => { e.stopPropagation(); handleBookBus(leg); }}>
                                                                                Book Bus
                                                                            </button>
                                                                            {getBusPromoDesc(card) && (
                                                                                <span className="flight-deal-badge"><span className="flight-deal-dot"></span>{getBusPromoDesc(card)}</span>
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <button className={`book-leg-btn ${leg.type}`} onClick={(e) => { e.stopPropagation(); if (leg.booking_link) window.open(leg.booking_link, '_blank', 'noopener,noreferrer'); }}>
                                                                            {leg.type === 'train' ? 'Book on IRCTC' : `Book ${leg.type.charAt(0).toUpperCase() + leg.type.slice(1)}`}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {(leg.footerText || (index === card.legs.length - 1 && card.explanation)) && (
                                                            <div className="leg-footer-msg">
                                                                {[
                                                                    ...(index === card.legs.length - 1 && card.explanation ? [card.explanation] : []),
                                                                    ...(leg.footerText ? leg.footerText.split('\n') : [])
                                                                ].filter(line => line.trim() !== '').map((line, i) => <p key={i}>{line}</p>)}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="leg-card location-leg layover">
                                                        <div className="leg-header">
                                                            <span className="leg-title">{leg.title || leg.location || 'Layover'}</span>
                                                            {leg.layoverCab && (
                                                                <span className="cab-selected-price">{formatPrice(selectedCabPrice(leg.layoverCab, cabSel[`l${index}`]))}</span>
                                                            )}
                                                            <span className="leg-mode">Layover</span>
                                                        </div>
                                                        <div className="leg-body">
                                                            <p className="location-desc" dangerouslySetInnerHTML={{ __html: (leg.description || '').replace(/(\d+ hr \d+ min)/, '<strong>$1</strong>') }}></p>
                                                            <CabOptions
                                                                cab={leg.layoverCab}
                                                                selected={cabSel[`l${index}`]}
                                                                onSelect={(c) => setCabSel(prev => ({ ...prev, [`l${index}`]: c }))}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </React.Fragment>
                                ))}

                                {(card.destCommentWeb || card.destCab) && (
                                    <div className="timeline-item">
                                        <div className="timeline-connector">
                                            <div className="node-icon">
                                                <img src="/icon3.svg" alt="Commute" style={{ width: 24, height: 24 }} />
                                            </div>
                                        </div>
                                        <div className="leg-content">
                                            <div className="leg-card location-leg commute-leg">
                                                <div className="leg-header">
                                                    <span className="leg-title">{card.destCommentWebTitle || 'Reach Destination'}</span>
                                                    {card.destCab && (
                                                        <span className="cab-selected-price">{formatPrice(selectedCabPrice(card.destCab, cabSel.dest))}</span>
                                                    )}
                                                    <span className="leg-mode">Commute</span>
                                                </div>
                                                <div className="leg-body">
                                                    {card.destCommentWeb && (
                                                        <p className="location-desc">{card.destCommentWeb}</p>
                                                    )}
                                                    <CabOptions
                                                        cab={card.destCab}
                                                        selected={cabSel.dest}
                                                        onSelect={(c) => setCabSel(prev => ({ ...prev, dest: c }))}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
            <LoadingModal
                isOpen={isBookingLoading}
                title="Processing Your Booking"
                message="We are fetching the latest fare and availability for you. This will only take a moment."
                showWarning={true}
                warningText="Please do not refresh this page or use the back button while we process your request."
            />
        </div>
    );
};

export default FlightMultiResultCard;
